import { FetchMethod, UrlBuilderParams, useCodeerApi } from "../useCodeerApi"
import {
  UserRoleSchema,
  OrganizationSchema,
  transToUser,
  transToPermissions,
} from "./types"
import { Organization, OrganizationRole, Response } from "@/types"

export interface OrganizationResponse extends OrganizationSchema {}

export interface UpdateOrganizationRequest {
  name?: string
}

export interface UpdateOrganizationAvatarRequest {
  avatar: File
}

export interface AddOrganizationUsersRequest {
  user_ids: number[]
  workspace_id: string
}

export interface RemoveOrganizationUsersRequest {
  user_ids: number[]
}

export interface UserRoleResponse extends UserRoleSchema {}

export interface UpdateOrganizationUserRoleRequest {
  user_id: number
  role_name: string
}

export interface OrganizationInvitationCapacityResponse {
  used: number
  limit?: number | null
  remaining?: number | null
  at_limit: boolean
}

export interface OrganizationInvitationCapacity {
  used: number
  limit: number | null
  remaining: number | null
  atLimit: boolean
}

export interface OrganizationInvitationDraftItemRequest {
  email: string
  workspace_id: string
}

export interface OrganizationInvitationBatchPreviewRequest {
  items: OrganizationInvitationDraftItemRequest[]
}

export interface OrganizationInvitationEmailPreviewResponse {
  subject: string
  body_text: string
}

export interface OrganizationInvitationPreviewItemResponse {
  action: "direct_add" | "email_invitation"
  email: string
  workspace_id: string
  workspace_name: string
  organization_name: string
  existing_user_name?: string | null
  existing_user_email?: string | null
  previews: Record<string, OrganizationInvitationEmailPreviewResponse>
  is_resend: boolean
}

export interface OrganizationInvitationEmailPreview {
  subject: string
  bodyText: string
}

export interface OrganizationInvitationPreviewItem {
  action: "direct_add" | "email_invitation"
  email: string
  workspaceId: string
  workspaceName: string
  organizationName: string
  existingUserName?: string | null
  existingUserEmail?: string | null
  previews: Record<string, OrganizationInvitationEmailPreview>
  isResend: boolean
}

export interface OrganizationInvitationBatchPreviewResponse {
  items: OrganizationInvitationPreviewItemResponse[]
  invite_capacity: OrganizationInvitationCapacityResponse
}

export interface OrganizationInvitationBatchPreview {
  items: OrganizationInvitationPreviewItem[]
  inviteCapacity: OrganizationInvitationCapacity
}

export interface ConfirmOrganizationInvitationItemRequest {
  email: string
  workspace_id: string
  language: string
}

export interface ConfirmOrganizationInvitationBatchRequest {
  items: ConfirmOrganizationInvitationItemRequest[]
}

export interface OrganizationInvitationBatchConfirmResultResponse {
  direct_add_count: number
  invitation_email_count: number
  invitation_resend_count: number
}

export interface OrganizationInvitationBatchConfirmResult {
  directAddCount: number
  invitationEmailCount: number
  invitationResendCount: number
}

export interface PendingOrganizationInvitationResponse {
  id: string
  email: string
  workspace_id: string
  workspace_name: string
  invited_by_name: string
  invited_by_email: string
  status: "pending" | "expired"
  created_at: string
  expires_at: string
}

export interface PendingOrganizationInvitation {
  id: string
  email: string
  workspaceId: string
  workspaceName: string
  invitedByName: string
  invitedByEmail: string
  status: "pending" | "expired"
  createdAt: string
  expiresAt: string
}

export interface OrganizationKnowledgeBaseStorageResponse {
  bytes_used: number
  bytes_limit?: number | null
  bytes_remaining?: number | null
  at_limit: boolean
}

export interface OrganizationKnowledgeBaseStorage {
  bytesUsed: number
  bytesLimit: number | null
  bytesRemaining: number | null
  atLimit: boolean
}

const buildOrganizationKnowledgeBaseStorageUrl = (params: UrlBuilderParams) =>
  `/organizations/${params.organizationId}/knowledge-base-storage`

const buildRevokePendingOrganizationInvitationUrl = (
  params: UrlBuilderParams
) =>
  `/organizations/${params.organizationId}/invitations/${params.invitationId}/revoke`

const buildResendPendingOrganizationInvitationUrl = (
  params: UrlBuilderParams
) =>
  `/organizations/${params.organizationId}/invitations/${params.invitationId}/resend`

export const organizationResponseToOrganization = (
  resp: OrganizationResponse
): Organization => {
  return {
    id: resp.id,
    name: resp.name,
    avatar: resp.avatar,
    owner: transToUser(resp.owner),
    myRole: resp.my_permissions.role as OrganizationRole,
    myPermissions: new Set(transToPermissions(resp.my_permissions)),
  }
}

export { transToMember as userRoleResponseToMember } from "./types"

export const organizationKnowledgeBaseStorageResponseToStorage = (
  resp: OrganizationKnowledgeBaseStorageResponse
): OrganizationKnowledgeBaseStorage => {
  return {
    bytesUsed: resp.bytes_used,
    bytesLimit: resp.bytes_limit ?? null,
    bytesRemaining: resp.bytes_remaining ?? null,
    atLimit: resp.at_limit,
  }
}

export const organizationInvitationCapacityResponseToCapacity = (
  resp: OrganizationInvitationCapacityResponse
): OrganizationInvitationCapacity => ({
  used: resp.used,
  limit: resp.limit ?? null,
  remaining: resp.remaining ?? null,
  atLimit: resp.at_limit,
})

export const organizationInvitationPreviewItemResponseToPreviewItem = (
  resp: OrganizationInvitationPreviewItemResponse
): OrganizationInvitationPreviewItem => ({
  action: resp.action,
  email: resp.email,
  workspaceId: resp.workspace_id,
  workspaceName: resp.workspace_name,
  organizationName: resp.organization_name,
  existingUserName: resp.existing_user_name ?? null,
  existingUserEmail: resp.existing_user_email ?? null,
  previews: Object.fromEntries(
    Object.entries(resp.previews || {}).map(([locale, preview]) => [
      locale,
      {
        subject: preview.subject,
        bodyText: preview.body_text,
      },
    ])
  ),
  isResend: resp.is_resend,
})

export const organizationInvitationBatchPreviewResponseToPreview = (
  resp: OrganizationInvitationBatchPreviewResponse
): OrganizationInvitationBatchPreview => ({
  items: resp.items.map(organizationInvitationPreviewItemResponseToPreviewItem),
  inviteCapacity: organizationInvitationCapacityResponseToCapacity(
    resp.invite_capacity
  ),
})

export const organizationInvitationBatchConfirmResultResponseToResult = (
  resp: OrganizationInvitationBatchConfirmResultResponse
): OrganizationInvitationBatchConfirmResult => ({
  directAddCount: resp.direct_add_count,
  invitationEmailCount: resp.invitation_email_count,
  invitationResendCount: resp.invitation_resend_count,
})

export const pendingOrganizationInvitationResponseToInvitation = (
  resp: PendingOrganizationInvitationResponse
): PendingOrganizationInvitation => ({
  id: resp.id,
  email: resp.email,
  workspaceId: resp.workspace_id,
  workspaceName: resp.workspace_name,
  invitedByName: resp.invited_by_name,
  invitedByEmail: resp.invited_by_email,
  status: resp.status,
  createdAt: resp.created_at,
  expiresAt: resp.expires_at,
})

export const useGetOrganizationsApi = () => {
  return useCodeerApi<Response<OrganizationResponse[]>>(
    "/organizations",
    FetchMethod.Get
  )
}

export const useUpdateOrganizationApi = (organizationId: string) => {
  return useCodeerApi<
    Response<OrganizationResponse>,
    UpdateOrganizationRequest
  >(`/organizations/${organizationId}`, FetchMethod.Put)
}

export const useUpdateOrganizationAvatarApi = (organizationId: string) => {
  return useCodeerApi<
    Response<OrganizationResponse>,
    UpdateOrganizationAvatarRequest
  >(`/organizations/${organizationId}/avatar`, FetchMethod.Post)
}

export const useGetOrganizationUsersApi = (organizationId: string) => {
  return useCodeerApi<Response<UserRoleResponse[]>>(
    `/organizations/${organizationId}/users`,
    FetchMethod.Get
  )
}

export const useGetOrganizationInvitationCapacityApi = (
  organizationId: string
) => {
  return useCodeerApi<Response<OrganizationInvitationCapacityResponse>>(
    `/organizations/${organizationId}/invitations/capacity`,
    FetchMethod.Get
  )
}

export const useBatchPreviewOrganizationInvitationApi = (
  organizationId: string
) => {
  return useCodeerApi<
    Response<OrganizationInvitationBatchPreviewResponse>,
    OrganizationInvitationBatchPreviewRequest
  >(`/organizations/${organizationId}/invitations/preview`, FetchMethod.Post)
}

export const useConfirmOrganizationInvitationBatchApi = (
  organizationId: string
) => {
  return useCodeerApi<
    Response<OrganizationInvitationBatchConfirmResultResponse>,
    ConfirmOrganizationInvitationBatchRequest
  >(`/organizations/${organizationId}/invitations/confirm`, FetchMethod.Post)
}

export const useGetPendingOrganizationInvitationsApi = (
  organizationId: string
) => {
  return useCodeerApi<Response<PendingOrganizationInvitationResponse[]>>(
    `/organizations/${organizationId}/invitations/pending`,
    FetchMethod.Get
  )
}

export const useRevokePendingOrganizationInvitationApi = (
  organizationId: string
) => {
  return useCodeerApi<Response<null>>(
    `/organizations/${organizationId}/invitations/__placeholder__/revoke`,
    FetchMethod.Post,
    true,
    buildRevokePendingOrganizationInvitationUrl
  )
}

export const useResendPendingOrganizationInvitationApi = (
  organizationId: string
) => {
  return useCodeerApi<Response<PendingOrganizationInvitationResponse>>(
    `/organizations/${organizationId}/invitations/__placeholder__/resend`,
    FetchMethod.Post,
    true,
    buildResendPendingOrganizationInvitationUrl
  )
}

export const useGetOrganizationKnowledgeBaseStorageApi = () => {
  return useCodeerApi<Response<OrganizationKnowledgeBaseStorageResponse>>(
    "/organizations/__placeholder__/knowledge-base-storage",
    FetchMethod.Get,
    true,
    buildOrganizationKnowledgeBaseStorageUrl
  )
}

export const useAddOrganizationUsersApi = (organizationId: string) => {
  return useCodeerApi<Response<UserRoleResponse>, AddOrganizationUsersRequest>(
    `/organizations/${organizationId}/users/add`,
    FetchMethod.Post
  )
}

export const useRemoveOrganizationUsersApi = (organizationId: string) => {
  return useCodeerApi<
    Response<UserRoleResponse>,
    RemoveOrganizationUsersRequest
  >(`/organizations/${organizationId}/users/remove`, FetchMethod.Post)
}

export const useLeaveOrganizationUsersApi = (organizationId: string) => {
  return useCodeerApi<
    Response<UserRoleResponse>,
    RemoveOrganizationUsersRequest
  >(`/organizations/${organizationId}/users/leave`, FetchMethod.Post)
}

export const useUpdateOrganizationUserRoleApi = (
  organizationId: string,
  userId: string
) => {
  return useCodeerApi<
    Response<UserRoleResponse>,
    UpdateOrganizationUserRoleRequest
  >(`/organizations/${organizationId}/users/${userId}/role`, FetchMethod.Put)
}
